'use client';

import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { mediaUrl, StorageBuckets } from '@kit/shared/storage';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { cn } from '@kit/ui/utils';

/**
 * Self-defense against the recurring "raw storage path in AvatarImage"
 * bug (2026-07-05): `profiles.profile_photo_url` is a bucket-relative
 * dancer-media PATH, not a URL (migration 20260205110847). Several loaders
 * forgot to sign it before passing it here, and a raw path 404s. Rather
 * than trust every caller to resolve upstream, resolve here: an already-
 * resolved value (http(s) URL, or a `/api/...` proxy path) passes through
 * untouched; a bare bucket-relative path is routed through `mediaUrl` to
 * the token-free `/api/media/cover` proxy. `mediaUrl` is client-safe for
 * this bucket (it only builds a URL, no secrets).
 */
function resolveAvatarSrc(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  if (value.startsWith('/')) return value; // already a proxy / app path
  return mediaUrl(
    { bucket: StorageBuckets.DANCER_MEDIA, path: value, visibility: 'public' },
    'THUMBNAIL',
  );
}

export interface DancerAvatarProps {
  /** Primary headshot URL or a bucket-relative dancer-media path — a bare
   * path is resolved internally (see `resolveAvatarSrc`), so callers may
   * pass either a signed URL or the raw `primary_headshot_url` value. */
  primaryHeadshotUrl?: string | null;
  /** Profile photo URL or a bucket-relative `profiles.profile_photo_url`
   * path — a bare path is resolved internally. */
  profilePhotoUrl?: string | null;
  /** First name for fallback initials */
  firstName?: string | null;
  /** Last name for fallback initials */
  lastName?: string | null;
  /** Full name for alt text (optional, computed from first/last if not provided) */
  fullName?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-16 w-16 text-lg',
  xl: 'h-24 w-24 text-2xl',
};

/**
 * Reusable dancer avatar component that handles:
 * - Primary headshot URL (from artist_media or profile_photo_url cache)
 * - Profile photo URL fallback (from profiles.profile_photo_url)
 * - Initials fallback when no image is available
 * - Image load error handling
 *
 * Memoized for performance in large lists (dancer tables, cast grids).
 *
 * @example
 * ```tsx
 * <DancerAvatar
 *   primaryHeadshotUrl={dancer.primary_headshot_url}
 *   profilePhotoUrl={dancer.profile_photo_url}
 *   firstName={dancer.first_name}
 *   lastName={dancer.last_name}
 *   size="lg"
 * />
 * ```
 */
export const DancerAvatar = memo(function DancerAvatar({
  primaryHeadshotUrl,
  profilePhotoUrl,
  firstName,
  lastName,
  fullName: providedFullName,
  size = 'md',
  className,
}: DancerAvatarProps) {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);

  // Compute display values
  const fullName =
    providedFullName ||
    `${firstName || ''} ${lastName || ''}`.trim() ||
    t('dancer:dancerAvatar.unknown');
  const initials =
    `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';

  // Select best available image URL (primary headshot takes precedence),
  // resolving a bare bucket-relative path to a fetchable proxy URL.
  const imageUrl =
    resolveAvatarSrc(primaryHeadshotUrl) ?? resolveAvatarSrc(profilePhotoUrl);

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {!imageError && imageUrl && (
        <AvatarImage
          src={imageUrl}
          alt={fullName}
          onError={() => setImageError(true)}
        />
      )}
      <AvatarFallback className="bg-primary text-primary-foreground">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
});
